import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Shared scroll-lock manager (module-scoped).
 * Goal: prevent iOS Safari flashes/jumps caused by toggling body position:fixed.
 */
const SCROLL_LOCK_ATTR = 'scroll-lock';

// Any open overlay containers where scrolling should be allowed while background is locked.
const OVERLAY_ALLOW_SCROLL_SELECTOR = 'dialog[open], details[open][scroll-lock]';

let scrollLockCount = 0;
let savedScrollY = 0;

let savedHtmlOverflow = '';
let savedBodyOverflow = '';
let savedBodyPaddingRight = '';
let savedBodyTouchAction = '';

let activeScrollbarCompensation = 0;

/** @type {(e: TouchEvent) => void | null} */
let touchMoveHandler = null;

function lockScroll({ scrollY, scrollbarCompensation = 0 } = {}) {
  scrollLockCount += 1;
  if (scrollLockCount !== 1) return;

  savedScrollY = typeof scrollY === 'number' ? scrollY : window.scrollY;

  savedHtmlOverflow = document.documentElement.style.overflow;
  savedBodyOverflow = document.body.style.overflow;
  savedBodyPaddingRight = document.body.style.paddingRight;
  savedBodyTouchAction = document.body.style.touchAction;

  activeScrollbarCompensation = Math.max(Number(scrollbarCompensation) || 0, 0);

  document.documentElement.setAttribute(SCROLL_LOCK_ATTR, '');

  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';

  if (activeScrollbarCompensation) {
    document.body.style.paddingRight = `${activeScrollbarCompensation}px`;
    document.documentElement.style.setProperty('--scrollbar-compensation', `${activeScrollbarCompensation}px`);
  }

  document.body.style.touchAction = 'none';

  touchMoveHandler = (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (target && target.closest && target.closest(OVERLAY_ALLOW_SCROLL_SELECTOR)) return;
    event.preventDefault();
  };

  document.addEventListener('touchmove', touchMoveHandler, { passive: false });
}

function unlockScroll() {
  if (scrollLockCount === 0) return;

  scrollLockCount -= 1;
  if (scrollLockCount !== 0) return;

  document.documentElement.style.overflow = savedHtmlOverflow;
  document.body.style.overflow = savedBodyOverflow;
  document.body.style.paddingRight = savedBodyPaddingRight;
  document.documentElement.style.removeProperty('--scrollbar-compensation');
  document.body.style.touchAction = savedBodyTouchAction;

  document.documentElement.removeAttribute(SCROLL_LOCK_ATTR);

  if (touchMoveHandler) {
    document.removeEventListener('touchmove', touchMoveHandler);
    touchMoveHandler = null;
  }

  activeScrollbarCompensation = 0;

  requestAnimationFrame(() => {
    window.scrollTo({ top: savedScrollY, left: 0, behavior: 'auto' });
  });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #scrollbarCompensation = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    this.#scrollbarCompensation = Math.max(scrollbarWidth, 0);

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll({ scrollY, scrollbarCompensation: this.#scrollbarCompensation });

      const showDialog = () => {
        if (typeof dialog.showModal === 'function') {
          try {
            dialog.showModal();
            return;
          } catch (error) {
            console.warn('Dialog showModal blocked, falling back to show()', error);
          }
        }
        dialog.show();
      };

      showDialog();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.#scrollbarCompensation = 0;

    unlockScroll();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
          lockScroll({ scrollY: window.scrollY, scrollbarCompensation: Math.max(scrollbarWidth, 0) });
        } else {
          unlockScroll();
        }
      }
    }
  },
  { capture: true }
);
